import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import uniqBy from 'lodash/uniqBy';
import cx from 'classnames';

import { fullName, capitalize } from 'utils';
import Container from 'common/container';
import Button from 'common/button';
import useBreakpoint from 'hooks/use-breakpoint';
import ContributeContext from '../context';
import ContributeHeader from '../header';
import SelectFundraiserDropdown from '../select-fundraiser/dropdown';
import StepTitle from '../step-title';
import TypeCard from './type-card';

const GET_FUNDRAISER = gql`
    query GetFundaiser($username: String!, $campaignId: ID!) {
        fundraiserByUsername(username: $username, campaignId: $campaignId) {
            id
            user {
                id
                firstName
                lastName
                username
            }
        }
    }
`;

const SelectType = ({ onTypeChange, onFundraiserChange, onContinue }) => {
    const { campaign, fundraisers, referrer, type } = useContext(ContributeContext);
    const [expanded, setExpanded] = useState(type);
    const { md } = useBreakpoint();
    const mobile = !md;

    const itemQuery = useQuery(GET_FUNDRAISER, {
        variables: {
            username: referrer,
            campaignId: campaign.id,
        },
        skip: !referrer,
    });
    const itemResult = itemQuery.data?.fundraiserByUsername;

    // The list query won't return any fundraisers marked as "hidden"
    // but the initial referrer may be set to such a user. To safeguard
    // this case, we manually merge in the selected referrer
    const fundraiserList = uniqBy([...(itemResult ? [itemResult] : []), ...fundraisers], 'id');

    // If the pre-set referrer isn't actually a fundraiser, reset it
    useEffect(() => {
        const match = fundraiserList.find(x => x.user.username === referrer);
        if (!itemQuery.loading && !match) onFundraiserChange(null);
    }, [itemQuery.loading, referrer]);

    // Get finalized events remaining from performances.length instead of
    // eventRemainingCount for old campaigns that don't have proper stats
    const canPledge =
        campaign.performances.filter(x => x.isFinal).length < campaign.performances.length;

    const singularMetric = campaign.metricName?.singular;
    const pluralMetric = campaign.metricName?.plural;
    const pluralEvent = campaign.metricName?.plural;

    return (
        <Container className="pt-8 pb-16 flex-1 flex flex-col justify-start max-w-xl">
            <div className="md:my-4">
                <ContributeHeader />
            </div>
            {fundraiserList.length > 0 && (
                <div className="mt-8">
                    <SelectFundraiserDropdown
                        selected={referrer}
                        fundraisers={fundraiserList.map(x => ({
                            name: fullName(x.user.firstName, x.user.lastName),
                            username: x.user.username,
                        }))}
                        onChange={onFundraiserChange}
                    />
                </div>
            )}
            <div className="mt-12 rounded-lg lg:bg-white lg:shadow lg:p-16">
                <StepTitle title="How would you like to contribute?" />

                <div className="md:flex -ml-12 -mt-4 items-stretch">
                    <div className="w-full md:w-1/2 pl-12 pt-4">
                        <TypeCard
                            title={`Pledge per ${singularMetric}`}
                            description={
                                canPledge
                                    ? `Make a contribution based on the number of
                            ${pluralMetric} recorded during this campaign.`
                                    : `You can no longer pledge per ${singularMetric} as all the ${pluralEvent} have already passed.`
                            }
                            buttonLabel={`Make a pledge per ${singularMetric}`}
                            expanded={!mobile || expanded === 'pledge'}
                            active={type === 'pledge'}
                            disabled={!canPledge}
                            onClick={() => {
                                setExpanded('pledge');

                                if (canPledge) {
                                    onTypeChange('pledge');
                                    if (!mobile) onContinue();
                                }
                            }}
                        />
                    </div>
                    <div className="w-full md:w-1/2 pl-12 pt-4">
                        <TypeCard
                            title="One-time donation"
                            description={`Make a one-time, flat donation which is not affected by the
                            number of ${pluralMetric} recorded.`}
                            buttonLabel="Make a one-time donation"
                            expanded={!mobile || expanded === 'donation'}
                            active={type === 'donation'}
                            onClick={() => {
                                setExpanded('donation');
                                onTypeChange('donation');
                                if (!mobile) onContinue();
                            }}
                        />
                    </div>
                </div>

                {mobile && (
                    <Button
                        size="md"
                        color={!type ? 'light' : 'green'}
                        className={cx('mt-8 block w-full text-16 font-medium', {
                            'pointer-events-none': !type,
                        })}
                        onClick={onContinue}
                    >
                        Continue {type ? `to ${capitalize(type)}` : ''}
                    </Button>
                )}
            </div>
        </Container>
    );
};

SelectType.propTypes = {
    onTypeChange: PropTypes.func.isRequired,
    onFundraiserChange: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
};

export default SelectType;
